import { useState, useEffect, useMemo } from 'react';
import { BrushSettings } from '../../types/mapData';
import { Asset } from '../../types/asset';
import { assetsApi } from '../../services/api';
import { useEditor } from '../../contexts/EditorContext';
import { loadHotbarPresets, validateHotbarPresets, autoFillHotbar, setActivePreset, HotbarPresets, HOTBAR_PRESET_COUNT } from '../../services/hotbarService';
import Slider from '../../components/Slider';
import Checkbox from '../../components/Checkbox';
import './BrushPanel.css';

interface BrushPanelProps {
  settings: BrushSettings;
  onSettingsChange: (settings: BrushSettings) => void;
  showMaterialSelection?: boolean;
}

/**
 * Panel for brush tool settings including material, brush, size, and rotation
 * Shows hotbar (2x4 grid) for quick asset selection
 * @param settings - Current brush settings
 * @param onSettingsChange - Callback when settings change
 * @param showMaterialSelection - Whether to show material selection (default true)
 */
function BrushPanel({ settings, onSettingsChange, showMaterialSelection = true }: BrushPanelProps): JSX.Element {
  const { assetsVersion } = useEditor();
  const [materials, setMaterials] = useState<Asset[]>([]);
  const [brushes, setBrushes] = useState<Asset[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [materialsPresets, setMaterialsPresets] = useState<HotbarPresets>({ presets: [], activePreset: 0 });
  const [brushesPresets, setBrushesPresets] = useState<HotbarPresets>({ presets: [], activePreset: 0 });

  /**
   * Fetches materials and brushes (global assets)
   * Reloads when assetsVersion changes
   */
  useEffect(() => {
    const fetchAssets = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const [materialsRes, brushesRes] = await Promise.all([
          assetsApi.list('materials'),
          assetsApi.list('brushes')
        ]);
        setMaterials(materialsRes.assets);
        setBrushes(brushesRes.assets);

        // Validate and load hotbar presets (removes invalid asset references)
        const matNames = materialsRes.assets.map(a => a.name);
        const brushNames = brushesRes.assets.map(a => a.name);
        const validMatNames = new Set(matNames);
        const validBrushNames = new Set(brushNames);

        let matPresets = validateHotbarPresets('materials', validMatNames);
        let brushPresets = validateHotbarPresets('brushes', validBrushNames);

        // Auto-fill first hotbar preset if completely empty
        if (matPresets.presets[0]?.slots.every(s => s === null) && matNames.length > 0) {
          autoFillHotbar('materials', 0, matNames);
          matPresets = loadHotbarPresets('materials', validMatNames);
        }
        if (brushPresets.presets[0]?.slots.every(s => s === null) && brushNames.length > 0) {
          autoFillHotbar('brushes', 0, brushNames);
          brushPresets = loadHotbarPresets('brushes', validBrushNames);
        }

        setMaterialsPresets(matPresets);
        setBrushesPresets(brushPresets);
      } catch (err) {
        console.error('Failed to fetch assets:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAssets();
  }, [assetsVersion]);

  /**
   * Get active hotbar slots for materials
   */
  const activeMaterialSlots = useMemo(() => {
    const activePreset = materialsPresets.presets[materialsPresets.activePreset];
    if (!activePreset) return [];
    return activePreset.slots.map(name => {
      if (!name) return null;
      return materials.find(m => m.name === name) || null;
    });
  }, [materialsPresets, materials]);

  /**
   * Get active hotbar slots for brushes
   */
  const activeBrushSlots = useMemo(() => {
    const activePreset = brushesPresets.presets[brushesPresets.activePreset];
    if (!activePreset) return [];
    return activePreset.slots.map(name => {
      if (!name) return null;
      return brushes.find(b => b.name === name) || null;
    });
  }, [brushesPresets, brushes]);

  /**
   * Handle switching material preset
   */
  const handleMaterialPresetChange = (presetIndex: number): void => {
    const newPresets = setActivePreset('materials', presetIndex);
    setMaterialsPresets(newPresets);
  };

  /**
   * Handle switching brush preset
   */
  const handleBrushPresetChange = (presetIndex: number): void => {
    const newPresets = setActivePreset('brushes', presetIndex);
    setBrushesPresets(newPresets);
  };

  /**
   * Updates a single setting
   */
  const updateSetting = <K extends keyof BrushSettings>(key: K, value: BrushSettings[K]): void => {
    onSettingsChange({ ...settings, [key]: value });
  };

  if (isLoading) {
    return (
      <div className="brush-panel">
        <div className="brush-panel-loading">
          <i className="fas fa-spinner fa-spin"></i>
          <span>Loading assets...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="brush-panel">
      {showMaterialSelection && (
        <div className="brush-panel-section">
          <div className="brush-panel-section-title">
            <span>Material</span>
            <div className="brush-panel-preset-switcher">
              {Array.from({ length: HOTBAR_PRESET_COUNT }).map((_, i) => (
                <button
                  key={i}
                  className={`brush-panel-preset-btn ${materialsPresets.activePreset === i ? 'brush-panel-preset-btn--active' : ''}`}
                  onClick={() => handleMaterialPresetChange(i)}
                  title={`Preset ${i + 1}`}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          </div>
          {materials.length === 0 ? (
            <div className="brush-panel-empty">No materials available</div>
          ) : (
            <div className="brush-panel-hotbar">
              {activeMaterialSlots.map((material, index) => (
                <button
                  key={index}
                  className={`brush-panel-hotbar-slot ${material && settings.materialName === material.name ? 'brush-panel-hotbar-slot--selected' : ''} ${!material ? 'brush-panel-hotbar-slot--empty' : ''}`}
                  onClick={() => material && updateSetting('materialName', material.name)}
                  title={material?.label || 'Empty slot'}
                  disabled={!material}
                >
                  {material ? (
                    <img
                      src={assetsApi.getFileUrl('materials', material.name)}
                      alt={material.label}
                    />
                  ) : (
                    <span className="brush-panel-hotbar-slot-empty-icon"></span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="brush-panel-section">
        <div className="brush-panel-section-title">
          <span>Brush</span>
          <div className="brush-panel-preset-switcher">
            {Array.from({ length: HOTBAR_PRESET_COUNT }).map((_, i) => (
              <button
                key={i}
                className={`brush-panel-preset-btn ${brushesPresets.activePreset === i ? 'brush-panel-preset-btn--active' : ''}`}
                onClick={() => handleBrushPresetChange(i)}
                title={`Preset ${i + 1}`}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
        {brushes.length === 0 ? (
          <div className="brush-panel-empty">No brushes available</div>
        ) : (
          <div className="brush-panel-hotbar">
            {activeBrushSlots.map((brush, index) => (
              <button
                key={index}
                className={`brush-panel-hotbar-slot ${brush && settings.brushName === brush.name ? 'brush-panel-hotbar-slot--selected' : ''} ${!brush ? 'brush-panel-hotbar-slot--empty' : ''}`}
                onClick={() => brush && updateSetting('brushName', brush.name)}
                title={brush?.label || 'Empty slot'}
                disabled={!brush}
              >
                {brush ? (
                  <img
                    src={assetsApi.getFileUrl('brushes', brush.name)}
                    alt={brush.label}
                  />
                ) : (
                  <span className="brush-panel-hotbar-slot-empty-icon"></span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="brush-panel-section">
        <div className="brush-panel-section-title">Settings</div>
        <div className="brush-panel-settings">
          <Slider
            label="Size"
            value={settings.size}
            min={8}
            max={512}
            step={8}
            unit="px"
            onChange={(value) => updateSetting('size', value)}
          />

          <Slider
            label="Rotation"
            value={settings.rotation}
            min={0}
            max={359}
            step={1}
            unit="deg"
            onChange={(value) => updateSetting('rotation', value)}
          />

          <Slider
            label="Strength"
            value={Math.round(settings.strength * 100)}
            min={5}
            max={100}
            step={5}
            unit="%"
            onChange={(value) => updateSetting('strength', value / 100)}
          />

          <div className="brush-panel-rotation-mode">
            <label className="brush-panel-rotation-mode-label">Rotation mode</label>
            <div className="brush-panel-rotation-options">
              <Checkbox
                label="Per stroke"
                checked={settings.rotationMode === 'stroke'}
                onChange={(checked) => updateSetting('rotationMode', checked ? 'stroke' : 'fixed')}
              />
              <Checkbox
                label="Continuous"
                checked={settings.rotationMode === 'continuous'}
                onChange={(checked) => updateSetting('rotationMode', checked ? 'continuous' : 'fixed')}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BrushPanel;
